import React from "react";
import { FaGithub, FaLinkedin, FaDownload, FaEnvelope, FaAt } from "react-icons/fa";
import { BsQuestionLg } from "react-icons/bs";
import { motion } from "framer-motion";
import { useSocialLinks } from "../../hooks/useSocialLinks";
import { DownloadCVButton } from "../../cv";

// Mapeo de iconos
const iconMapping = {
    FaLinkedin: FaLinkedin,
    FaGithub: FaGithub,
    FaDownload: FaDownload,
    generic: BsQuestionLg,
    FaEnvelope: FaEnvelope,
    FaAt: FaAt,
};

// Estilos de botones por nombre
const buttonStyles = {
    LinkedIn: "text-blue-500 border-2 border-blue-500 hover:bg-blue-500",
    GitHub: "text-gray-500 border-2 border-gray-500 hover:bg-gray-500",
};

// Variantes de animación para los botones
const buttonVariants = {
    hidden: {
        opacity: 0,
        x: -50,
        scale: 0.8,
    },
    visible: (custom) => ({
        opacity: 1,
        x: 0,
        scale: 1,
        transition: {
            duration: 0.5,
            delay: custom * 0.2,
            ease: "easeOut",
        },
    }),
};

export default function SocialLinks() {
    const { socialLinks, loading } = useSocialLinks();

    if (loading) {
        return (
            <div className="flex flex-col sm:flex-row items-start gap-4 mt-8 mb-16">
                <div className="animate-pulse inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gray-300 dark:bg-gray-700">
                    <div className="w-5 h-5 bg-gray-400 dark:bg-gray-600 rounded-full"></div>
                    <div className="h-4 w-24 bg-gray-400 dark:bg-gray-600 rounded"></div>
                </div>
            </div>
        );
    }

    // Filtrar enlaces sociales (excluir "Descargar CV" ya que se renderiza aparte)
    const links = socialLinks.filter((link) => link.name !== "Descargar CV");
    const hasDownloadCV = socialLinks.some((link) => link.name === "Descargar CV");

    return (
        <motion.div
            className="flex flex-col sm:flex-row items-start gap-4 mt-8 mb-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
        >
            {/* Botones de redes sociales */}
            {links.map((link, index) => {
                const IconComponent = iconMapping[link.icon] || iconMapping.generic;

                return (
                    <motion.a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        custom={index}
                        variants={buttonVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        className={`
                            inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3
                            font-bold rounded-lg transition-all duration-300 hover:text-white
                            focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-950
                            shadow-lg hover:shadow-xl
                            ${buttonStyles[link.name] || buttonStyles["GitHub"]}
                        `}
                    >
                        {IconComponent && <IconComponent className="w-5 h-5" />}
                        {link.name}
                    </motion.a>
                );
            })}

            {/* Botón de descarga de CV dinámico */}
            {hasDownloadCV && (
                <DownloadCVButton
                    motionProps={{
                        custom: links.length,
                        variants: buttonVariants,
                        initial: "hidden",
                        animate: "visible",
                        whileHover: { scale: 1.05, y: -2 },
                        whileTap: { scale: 0.95 },
                    }}
                />
            )}
        </motion.div>
    );
}
